import { db } from '@/db/client';
import { GameDefinitionSchema, type GameDefinition } from '@/schemas';

export interface MergeResult {
  applied: number;
  replaced: number;
}

/**
 * Compare two game definitions for last-write-wins conflict resolution.
 * Returns true if incoming definition should replace existing definition.
 */
function shouldReplaceGameDefinition(existing: GameDefinition, incoming: GameDefinition): boolean {
  // Use updated_at for conflict resolution (last-write-wins)
  if (incoming.updatedAt !== existing.updatedAt) {
    return incoming.updatedAt > existing.updatedAt;
  }

  // If updated_at is equal, use created_at as tie-breaker
  if (incoming.createdAt !== existing.createdAt) {
    return incoming.createdAt > existing.createdAt;
  }

  // If both are equal, use ID as final tie-breaker (deterministic)
  return incoming.id > existing.id;
}

export async function mergeRemoteGameDefinitions(incoming: GameDefinition[]): Promise<MergeResult> {
  if (incoming.length === 0) return { applied: 0, replaced: 0 };
  const validated = incoming.map((d) => GameDefinitionSchema.parse(d));
  let applied = 0;
  let replaced = 0;

  await db.transaction('rw', db.gameDefinitions, async () => {
    for (const definition of validated) {
      const existing = await db.gameDefinitions.get(definition.id);
      if (existing) {
        // Conflict resolution: last-write-wins (updated_at)
        if (shouldReplaceGameDefinition(existing, definition)) {
          await db.gameDefinitions.put(definition);
          replaced += 1;
        }
        // Otherwise keep existing definition
      } else {
        await db.gameDefinitions.add(definition);
        applied += 1;
      }
    }
  });

  return { applied, replaced };
}
